import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MarketplaceSellerProfile } from '../../../slices/profiles-data-slice/models/marketplace-seller-profile';
import { OwnershipTypeOptions } from '../../../slices/marketplace/timeline/models/enums/ownership-type.enum';
import {
    LocationTypeOptions,
    locationTypeTooltipHtml,
} from '../../../slices/marketplace/timeline/models/enums/location-type.enum';
import InfoTooltip from '../../info-tooltip/info-tooltip';
import SortingSymbol from '../../SortingSymbol/sorting-symbol';
import BootstrapTable from '../../react-bootstrap-table-next/react-bootstrap-table2';
import ImageCarousel from '../../ImageCarousel/ImageCarousel';
import './DisclosedProfile.scss';
import { RevenueGrowthRateOptions } from '../../../slices/marketplace/timeline/models/enums/revenue-growth.enum';
import { EntityLocationInfo } from '../../../slices/profiles-data-slice/models/entity-location-info';
import { useServiceFocuses } from '../../../custom-hooks/useServiceFocuses';
import {
    getOtherFocus,
    getServiceFocusById,
    isOtherFocus,
} from '../../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';
import { StarRating } from '../../StarRating/StarRating';
import { NotificationFlag } from '../../NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../store/selectors';
import { RefreshNotificationFlags } from '../../../slices/notification-flags/refresh-notification-flags';
import {
    getCarouselImages,
    resolveEffectiveImagesLocationId,
} from './locationImages';

interface DisclosedProfileProps {
    profileData?: MarketplaceSellerProfile;
    timelineId?: number;
}

const RATING_PLATFORMS: {
    key: 'google' | 'facebook' | 'yelp';
    label: string;
}[] = [
    { key: 'google', label: 'Google' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'yelp', label: 'Yelp' },
];

const DisclosedProfile: React.FC<DisclosedProfileProps> = (props) => {
    const { profileData, timelineId } = props;
    const serviceFocuses = useServiceFocuses();
    const [activePracticeIndex, setActivePracticeIndex] = useState(0);

    const practices = profileData?.practiceProfiles || [];
    const activePractice = practices[activePracticeIndex];
    const practiceLocations = activePractice?.practiceLocations || [];

    // "Show Images From" selection — falls back to the first location when nothing
    // is selected yet or the selection belongs to another practice tab.
    const [selectedImagesLocationId, setSelectedImagesLocationId] =
        useState<string>('');
    const effectiveImagesLocationId = resolveEffectiveImagesLocationId(
        practiceLocations,
        selectedImagesLocationId
    );
    const practiceImages = getCarouselImages(
        practiceLocations,
        activePractice?.uploads,
        effectiveImagesLocationId
    );

    const notificationFlags = useStateSelector(
        (s) => s.notificationFlags.flags
    );
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch() as any;
    const activePracticeIdRef = useRef<string>(practices[0]?.id ?? '');
    const readPracticeNotificationsRef = useRef<(practiceId: string) => void>(
        () => {}
    );

    const readPracticeNotifications = useCallback(
        (practiceId: string) => {
            if (!timelineId || !practiceId) return;
            const prefix = `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practiceId}`;
            const flagsToRead = notificationFlags.filter((f) =>
                f.eventCode.toLowerCase().startsWith(prefix.toLowerCase())
            );
            if (flagsToRead.length === 0) return;
            axios
                .delete('api/NotificationFlags', {
                    data: flagsToRead.map((f) => f.eventCode),
                })
                .then(() => dispatch(RefreshNotificationFlags()))
                .catch(() => {});
        },
        [timelineId, notificationFlags, dispatch, axios]
    );

    readPracticeNotificationsRef.current = readPracticeNotifications;

    useEffect(() => {
        activePracticeIdRef.current = practices[activePracticeIndex]?.id ?? '';
    }, [activePracticeIndex, practices]);

    useEffect(() => {
        return () => {
            readPracticeNotificationsRef.current(activePracticeIdRef.current);
        };
    }, []);

    const renderTabs = () => {
        if (practices.length <= 1) return null;

        return (
            <div className="RRT__container">
                <div className="RRT__tabs">
                    {practices.map((practice, index) => (
                        <div
                            key={practice.id}
                            className={
                                activePracticeIndex === index
                                    ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                    : 'RRT__tab'
                            }
                            onClick={() => {
                                if (index !== activePracticeIndex) {
                                    readPracticeNotifications(
                                        practices[activePracticeIndex]?.id ?? ''
                                    );
                                }
                                setActivePracticeIndex(index);
                            }}>
                            {practice.practiceName}
                            {timelineId && (
                                <NotificationFlag
                                    doNotRemove={true}
                                    includePrefix={true}
                                    eventCode={`${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practice.id}`}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const practiceFieldBadge = (field: string) =>
        timelineId && activePractice ? (
            <NotificationFlag
                doNotRemove={true}
                eventCode={`${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${activePractice.id}.${field}`}
            />
        ) : null;

    const locationFieldBadge = (
        locationId: string,
        field: string,
        includePrefix?: boolean
    ) =>
        timelineId && activePractice ? (
            <NotificationFlag
                doNotRemove={true}
                includePrefix={includePrefix}
                eventCode={`${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${activePractice.id}.PracticeLocations.${locationId}.${field}`}
            />
        ) : null;

    const locationRowBadge = (locationId: string) =>
        timelineId && activePractice ? (
            <NotificationFlag
                doNotRemove={true}
                includePrefix={true}
                eventCode={`${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${activePractice.id}.PracticeLocations.${locationId}`}
            />
        ) : null;

    const renderPracticeInfo = () => {
        if (!activePractice) return null;

        return (
            <div className="practice-info-section">
                <div className="section-title">Practice Information</div>
                <div className="info-grid">
                    <div className="info-item">
                        <span className="label label-with-badge">
                            Practice Name{practiceFieldBadge('PracticeName')}
                        </span>
                        <span className="value">
                            {activePractice.practiceName || ''}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label label-with-badge">
                            Owner Name{practiceFieldBadge('OwnerFullName')}
                        </span>
                        <span className="value">
                            {activePractice.ownerFullName || ''}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label label-with-badge">
                            Ownership Type
                            {practiceFieldBadge('PracticeOwnershipType')}
                        </span>
                        <span className="value">
                            {activePractice.practiceOwnershipType
                                ? OwnershipTypeOptions[
                                      activePractice.practiceOwnershipType
                                  ]
                                : ''}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label label-with-badge">
                            Service Focus
                            {practiceFieldBadge('PracticeFocusType')}
                        </span>
                        <span className="value">
                            {getServiceFocusLabel(
                                activePractice.practiceFocusType,
                                activePractice.otherPracticeType
                            )}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label label-with-badge">
                            Number of FTE DVMs{practiceFieldBadge('FteDvms')}
                        </span>
                        <span className="value">
                            {activePractice.fteDvms || ''}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label label-with-badge">
                            Revenue Growth Rate
                            {practiceFieldBadge('RevenueGrowthRate')}
                        </span>
                        <span className="value">
                            {activePractice.revenueGrowthRate
                                ? RevenueGrowthRateOptions[
                                      activePractice.revenueGrowthRate
                                  ]
                                : ''}
                        </span>
                    </div>
                    {activePractice?.averageRating != null && (
                        <div className="info-item info-item--rating">
                            <span className="label label-with-badge">
                                Average Rating
                                {practiceFieldBadge('AverageRating')}
                            </span>
                            <span className="value">
                                <StarRating
                                    value={activePractice.averageRating}
                                    size="md"
                                />
                            </span>
                        </div>
                    )}
                </div>

                <div className="description-section">
                    <span className="label label-with-badge">
                        Description{practiceFieldBadge('Description')}
                    </span>
                    <span className="value">{activePractice.description}</span>
                </div>
            </div>
        );
    };

    const getLocationsColumns = () => {
        return [
            ...getShowImagesFromColumn(),
            {
                dataField: 'dbaName',
                text: 'DBA Name',
                sort: true,
                formatter: (cellContent: string, row: { id: string }) => (
                    <span className="label-with-badge">
                        {cellContent}
                        {locationFieldBadge(row.id, 'DbaName')}
                        {locationRowBadge(row.id)}
                    </span>
                ),
            },
            {
                dataField: 'streetAddress',
                text: 'Street Address',
                sort: true,
                formatter: (cellContent: string, row: { id: string }) => (
                    <span className="label-with-badge">
                        {cellContent}
                        {locationFieldBadge(
                            row.id,
                            'AddressInfo.StreetAddress'
                        )}
                    </span>
                ),
            },
            {
                dataField: 'city',
                text: 'City',
                sort: true,
                formatter: (cellContent: string, row: { id: string }) => (
                    <span className="label-with-badge">
                        {cellContent}
                        {locationFieldBadge(row.id, 'AddressInfo.City')}
                    </span>
                ),
            },
            {
                dataField: 'state',
                text: 'State',
                sort: true,
                formatter: (cellContent: string, row: { id: string }) => (
                    <span className="label-with-badge">
                        {cellContent}
                        {locationFieldBadge(row.id, 'AddressInfo.State')}
                    </span>
                ),
            },
            {
                dataField: 'zipCode',
                text: 'Zip',
                sort: true,
                formatter: (cellContent: string, row: { id: string }) => (
                    <span className="label-with-badge">
                        {cellContent}
                        {locationFieldBadge(row.id, 'AddressInfo.ZipCode')}
                    </span>
                ),
            },
            {
                dataField: 'locationType',
                text: 'Location Type',
                sort: true,
                headerFormatter: (
                    _column: any,
                    _colIndex: any,
                    components: any
                ) => (
                    <span>
                        Location Type
                        <InfoTooltip
                            idText="location-type-disclosed-tooltip"
                            text=""
                            html={locationTypeTooltipHtml}
                            place="top"
                        />
                        <SortingSymbol
                            order={components.sortElement.props?.order}
                        />
                    </span>
                ),
                formatter: (cellContent: number, row: { id: string }) => (
                    <span className="label-with-badge">
                        {cellContent
                            ? LocationTypeOptions[
                                  cellContent as keyof typeof LocationTypeOptions
                              ]
                            : ''}
                        {locationFieldBadge(row.id, 'LocationType')}
                    </span>
                ),
            },
            {
                dataField: 'fteDvms',
                text: 'FTE DVMs',
                sort: true,
                formatter: (cellContent: string, row: { id: string }) => (
                    <span className="label-with-badge">
                        {cellContent}
                        {locationFieldBadge(row.id, 'FteDvms')}
                    </span>
                ),
            },
            {
                dataField: 'practiceFocusType',
                text: 'Service Focus',
                sort: true,
                formatter: (cellContent: number, row: EntityLocationInfo) => (
                    <span className="label-with-badge">
                        {getServiceFocusLabel(
                            cellContent,
                            row.otherPracticeType
                        )}
                        {locationFieldBadge(row.id, 'PracticeFocusType')}
                    </span>
                ),
            },
            ...getLocationRatingsColumns(),
        ];
    };

    const getShowImagesFromColumn = () => {
        // Radio column only makes sense when there is a choice to make.
        if (practiceLocations.length <= 1) {
            return [];
        }

        return [
            {
                dataField: 'showImagesFrom',
                text: 'Show Images From',
                sort: false,
                isDummyField: true,
                headerStyle: { minWidth: '110px' },
                // The table's Cell.shouldComponentUpdate only re-renders when the
                // row or formatExtraData changes — the selection must flow through
                // formatExtraData or the radios never visually update.
                formatExtraData: { selectedLocationId: effectiveImagesLocationId },
                formatter: (
                    _cellContent: unknown,
                    row: { id: string },
                    _rowIndex: number,
                    formatExtraData: { selectedLocationId?: string }
                ) => (
                    <input
                        type="radio"
                        className="show-images-from-radio"
                        name={`show-images-from-${activePractice?.id}`}
                        aria-label="Show images from this location"
                        checked={row.id === formatExtraData?.selectedLocationId}
                        onChange={() => setSelectedImagesLocationId(row.id)}
                    />
                ),
            },
        ];
    };

    const getLocationRatingsColumns = () => {
        const locations = activePractice?.practiceLocations || [];

        return RATING_PLATFORMS.filter((platform) =>
            locations.some(
                (location) => location.ratings?.[platform.key] != null
            )
        ).map((platform) => ({
            dataField: `${platform.key}Rating`,
            text: platform.label,
            sort: true,
            headerStyle: { minWidth: '90px' },
            style: { minWidth: '90px' },
            formatter: (
                cellContent: number | undefined,
                row: { id: string } & Record<string, any>
            ) => {
                if (cellContent == null) {
                    return <span />;
                }
                const reviewCount = row[`${platform.key}ReviewCount`];
                return (
                    <span className="location-rating-cell">
                        <span className="label-with-badge">
                            <StarRating value={cellContent} size="sm" />
                            {locationFieldBadge(
                                row.id,
                                `Ratings.${platform.label}`,
                                true
                            )}
                        </span>
                        {reviewCount != null && reviewCount > 0 && (
                            <span className="location-rating-cell__count">
                                ({reviewCount})
                            </span>
                        )}
                    </span>
                );
            },
        }));
    };

    const getServiceFocusLabel = (
        practiceFocusType?: number,
        otherPracticeType?: string
    ): string => {
        if (!practiceFocusType) {
            return '';
        }

        const isOther = isOtherFocus(
            serviceFocuses,
            activePractice.practiceFocusType
        );

        if (isOther) {
            return (
                otherPracticeType || getOtherFocus(serviceFocuses)?.name || ''
            );
        }

        return (
            getServiceFocusById(serviceFocuses, practiceFocusType)?.name || ''
        );
    };

    const getLocationsTableData = () => {
        if (!activePractice?.practiceLocations?.length) return [];

        return activePractice.practiceLocations.map((location) => ({
            id: location.id,
            streetAddress: location.addressInfo?.streetAddress || '',
            city: location.addressInfo?.city || '',
            state: location.addressInfo?.state || '',
            zipCode: location.addressInfo?.zipCode || '',
            locationType: location.locationType,
            dbaName: location.dbaName || '',
            fteDvms: location.fteDvms || '',
            practiceFocusType: location.practiceFocusType,
            otherPracticeType: location.otherPracticeType,
            googleRating: location.ratings?.google,
            googleReviewCount: location.ratings?.googleReviewCount,
            facebookRating: location.ratings?.facebook,
            facebookReviewCount: location.ratings?.facebookReviewCount,
            yelpRating: location.ratings?.yelp,
            yelpReviewCount: location.ratings?.yelpReviewCount,
        }));
    };

    const renderLocationsTable = () => {
        if (!activePractice?.practiceLocations?.length) return null;

        return (
            <div className="locations-section">
                <div className="locations-table">
                    <BootstrapTable
                        bootstrap4
                        keyField="id"
                        data={getLocationsTableData()}
                        columns={getLocationsColumns()}
                        noDataIndication="No locations available"
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="disclosed-profile">
            <div className="profile-content">
                {renderTabs()}
                <div className="profile-main-content">
                    <div className="left-section">{renderPracticeInfo()}</div>
                    <div className="right-section">
                        <ImageCarousel
                            key={effectiveImagesLocationId || 'no-location'}
                            images={practiceImages}
                            autoPlayInterval={5000}
                            showArrows={true}
                            showDots={true}
                            maxDots={5}
                            className="practice-carousel"
                        />
                    </div>
                </div>
                {renderLocationsTable()}
            </div>
        </div>
    );
};

export default DisclosedProfile;
